import { useEffect } from 'react';

/**
 * Scroll-reveal for the public landing page. Elements marked `.reveal` fade in
 * and rise as they enter the viewport (playing once), driven by an
 * IntersectionObserver.
 *
 * The hidden initial state in CSS is gated behind the `reveal-on` class, which
 * this hook adds only after the observer is wired — so if JS never runs, the
 * observer is unavailable, or the visitor prefers reduced motion, content is
 * rendered fully visible instead of stuck at opacity 0.
 *
 * A throttled scroll "sweep" backs up the observer for instant scroll jumps
 * (the header's `#pricing` / `#why` hash-nav, reload scroll-restore, fast
 * flicks) where an element can go from below to above the viewport without the
 * observer ever seeing it cross the trigger line. The sweep reveals anything
 * already at/above that line, guaranteeing nothing is left invisible on-screen.
 */
export function useReveal(): void {
  useEffect(() => {
    const root = document.getElementById('top');
    if (!root) return;

    const remaining = () => Array.from(root.querySelectorAll<HTMLElement>('.reveal:not(.is-visible)'));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || !('IntersectionObserver' in window)) {
      remaining().forEach((el) => el.classList.add('is-visible'));
      return;
    }

    root.classList.add('reveal-on');

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0, rootMargin: '0px 0px -15% 0px' },
    );
    remaining().forEach((el) => io.observe(el));

    // Safety sweep — reveal anything already at/above the trigger line (same
    // 85%-of-viewport line the observer's rootMargin uses) and stop listening
    // once everything has appeared.
    let raf = 0;
    const sweep = (): void => {
      raf = 0;
      const line = window.innerHeight * 0.85;
      const left = remaining();
      for (const el of left) {
        if (el.getBoundingClientRect().top < line) {
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      }
      if (remaining().length === 0) window.removeEventListener('scroll', onScroll);
    };
    const onScroll = (): void => {
      if (!raf) raf = requestAnimationFrame(sweep);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
      root.classList.remove('reveal-on');
    };
  }, []);
}
